import { useEffect, useState } from "react";
import { Heart, Share2, X } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { Reveal } from "@/components/Reveal";
import { ResponsiveImage } from "@/components/ResponsiveImage";
import { transformedImage } from "@/lib/image-url";
import { toast } from "sonner";
import { SITE } from "@/config/site";
import { incrementGalleryLike, incrementGalleryShare } from "@/lib/api/gallery-engagement.functions";

type Item = {
  id: string;
  image_url: string;
  thumbnail_url?: string | null;
  caption?: string | null;
  likes_count?: number | null;
  shares_count?: number | null;
};

const LIKED_KEY = "gallery_liked_ids_v1";

function getLikedSet(): Set<string> {
  if (typeof window === "undefined") return new Set();
  try { return new Set(JSON.parse(localStorage.getItem(LIKED_KEY) || "[]")); }
  catch { return new Set(); }
}

function saveLikedSet(s: Set<string>) {
  try { localStorage.setItem(LIKED_KEY, JSON.stringify([...s])); } catch {}
}

export function HomeGalleryPreview({ items }: { items: Item[] }) {
  const visible = items.slice(0, 8);
  const [counts, setCounts] = useState<Record<string, { likes: number; shares: number }>>(() =>
    Object.fromEntries(visible.map((g) => [g.id, { likes: g.likes_count ?? 0, shares: g.shares_count ?? 0 }])),
  );
  const [liked, setLiked] = useState<Set<string>>(new Set());
  const [lightbox, setLightbox] = useState<Item | null>(null);

  useEffect(() => { setLiked(getLikedSet()); }, []);

  async function handleLike(g: Item, e?: React.MouseEvent) {
    e?.stopPropagation();
    if (liked.has(g.id)) {
      toast.message("You already liked this photo");
      return;
    }
    const next = new Set(liked); next.add(g.id); setLiked(next); saveLikedSet(next);
    setCounts((c) => ({ ...c, [g.id]: { ...c[g.id], likes: (c[g.id]?.likes ?? 0) + 1 } }));
    try {
      const newCount = await incrementGalleryLike({ data: { id: g.id } });
      if (typeof newCount === "number") {
        setCounts((c) => ({ ...c, [g.id]: { ...c[g.id], likes: newCount } }));
      }
    } catch (err) { console.error(err); }
  }

  async function handleShare(g: Item, e?: React.MouseEvent) {
    e?.stopPropagation();
    const url = `${SITE.url}/gallery`;
    const title = g.caption || "Surer Adda With Sanchayeeta — Photo";
    try {
      if (typeof navigator !== "undefined" && (navigator as any).share) {
        await (navigator as any).share({ title, url });
      } else if (typeof navigator !== "undefined" && navigator.clipboard) {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied to clipboard");
      }
    } catch { /* user cancelled */ return; }
    setCounts((c) => ({ ...c, [g.id]: { ...c[g.id], shares: (c[g.id]?.shares ?? 0) + 1 } }));
    try {
      const newCount = await incrementGalleryShare({ data: { id: g.id } });
      if (typeof newCount === "number") {
        setCounts((c) => ({ ...c, [g.id]: { ...c[g.id], shares: newCount } }));
      }
    } catch (err) { console.error(err); }
  }

  function fmt(n: number) {
    if (n >= 1000) return `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k`;
    return String(n);
  }

  return (
    <section className="bg-secondary/30 py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="mb-10 flex items-end justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-primary">Moments</p>
            <h2 className="mt-2 text-3xl sm:text-4xl">From the Stage</h2>
          </div>
          <Link to="/gallery" className="hidden text-sm font-medium text-primary hover:underline sm:inline">Full gallery →</Link>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
          {visible.map((g, i) => {
            const c = counts[g.id] ?? { likes: 0, shares: 0 };
            const isLiked = liked.has(g.id);
            return (
              <Reveal key={g.id} delay={(i % 4) * 70} className="group relative overflow-hidden rounded-xl bg-card">
                <button
                  type="button"
                  onClick={() => setLightbox(g)}
                  className="block aspect-square w-full cursor-zoom-in overflow-hidden"
                  aria-label={g.caption ? `Open full photo: ${g.caption}` : "Open full gallery photo"}
                >
                  <ResponsiveImage
                    src={g.thumbnail_url || g.image_url}
                    alt={g.caption ?? "Sanchayeeta Seal live performance moment"}
                    widths={[200, 320, 480, 640, 800]}
                    sizes="(min-width: 768px) 25vw, (min-width: 640px) 33vw, 50vw"
                    fallbackWidth={480}
                    className="h-full w-full object-cover duration-500 hover:scale-105"
                  />
                </button>
                <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent p-2">
                  <div className="pointer-events-auto flex items-center justify-between gap-2 text-white">
                    <button
                      type="button"
                      onClick={(e) => handleLike(g, e)}
                      aria-pressed={isLiked}
                      aria-label={isLiked ? "Unlike photo" : "Like photo"}
                      className="inline-flex items-center gap-1 rounded-full bg-black/40 px-2.5 py-1 text-xs backdrop-blur transition hover:bg-black/60"
                    >
                      <Heart className={`h-3.5 w-3.5 ${isLiked ? "fill-rose-500 text-rose-500" : ""}`} />
                      <span>{fmt(c.likes)}</span>
                    </button>
                    <button
                      type="button"
                      onClick={(e) => handleShare(g, e)}
                      aria-label="Share photo"
                      className="inline-flex items-center gap-1 rounded-full bg-black/40 px-2.5 py-1 text-xs backdrop-blur transition hover:bg-black/60"
                    >
                      <Share2 className="h-3.5 w-3.5" />
                      <span>{fmt(c.shares)}</span>
                    </button>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
        <div className="mt-6 text-center sm:hidden">
          <Link to="/gallery" className="text-sm font-medium text-primary hover:underline">Full gallery →</Link>
        </div>
      </div>

      {lightbox && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-4"
          onClick={() => setLightbox(null)}
          role="dialog"
          aria-modal="true"
        >
          <button
            aria-label="Close photo viewer"
            className="absolute right-4 top-4 text-white"
            onClick={() => setLightbox(null)}
          >
            <X className="h-8 w-8" />
          </button>
          <div className="relative max-h-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
            <img
              src={transformedImage(lightbox.image_url, { width: 1600, quality: 85 })}
              alt={lightbox.caption ?? "Enlarged gallery photo"}
              className="max-h-[85vh] w-auto rounded-lg"
            />
            <div className="mt-4 flex items-center justify-center gap-3 text-white">
              <button
                type="button"
                onClick={(e) => handleLike(lightbox, e)}
                aria-pressed={liked.has(lightbox.id)}
                className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm backdrop-blur transition hover:bg-white/20"
              >
                <Heart className={`h-4 w-4 ${liked.has(lightbox.id) ? "fill-rose-500 text-rose-500" : ""}`} />
                <span>{fmt(counts[lightbox.id]?.likes ?? 0)} Likes</span>
              </button>
              <button
                type="button"
                onClick={(e) => handleShare(lightbox, e)}
                className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm backdrop-blur transition hover:bg-white/20"
              >
                <Share2 className="h-4 w-4" />
                <span>{fmt(counts[lightbox.id]?.shares ?? 0)} Shares</span>
              </button>
            </div>
            {lightbox.caption && (
              <p className="mt-3 text-center text-sm text-white/80">{lightbox.caption}</p>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

